"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MessageOutbox = void 0;
const utils_1 = require("../utils");
const OUTBOX_KEY = "hhn.message.outbox";
/**
 * Survives a reload or tab close, which every in-memory retry mechanism does
 * not. A record is written the instant a message enters PENDING, before the
 * emit, and dropped the moment it resolves.
 *
 * Held as an array, in send order: recovered messages must come back in the
 * order they were typed, and two sends in the same millisecond must both
 * survive rather than being collapsed or reordered by a timestamp sort.
 *
 * Configured once at bootstrap; a domain running without a store configured
 * degrades to in-memory-only behaviour rather than throwing.
 */
class MessageOutbox {
    constructor() {
        /**
         * Every mutation is a read-modify-write of one blob, so a burst of sends
         * would otherwise interleave and each write would clobber the record the
         * previous one just added. Serialising them is what keeps a rapid burst of
         * messages from silently losing all but the last.
         */
        this.mutations = Promise.resolve();
    }
    static getInstance() {
        if (!MessageOutbox.instance) {
            MessageOutbox.instance = new MessageOutbox();
        }
        return MessageOutbox.instance;
    }
    static configure(store) {
        MessageOutbox.getInstance().store = store;
    }
    isEnabled() {
        return !!this.store;
    }
    save(record) {
        if (!this.store) {
            return Promise.resolve();
        }
        return this.serialise(() => __awaiter(this, void 0, void 0, function* () {
            const records = yield this.read();
            const existing = records.findIndex(candidate => candidate.payload.messageId === record.payload.messageId);
            if (existing === -1) {
                records.push(record);
            }
            else {
                // Updated in place so a retry keeps its position in the send order.
                records[existing] = record;
            }
            yield this.write(records);
        }));
    }
    remove(messageId) {
        if (!this.store) {
            return Promise.resolve();
        }
        return this.serialise(() => __awaiter(this, void 0, void 0, function* () {
            const records = yield this.read();
            const remaining = records.filter(record => record.payload.messageId !== messageId);
            if (remaining.length === records.length) {
                return;
            }
            yield this.write(remaining);
        }));
    }
    listForChat(chatId) {
        if (!this.store) {
            return Promise.resolve([]);
        }
        return this.serialise(() => __awaiter(this, void 0, void 0, function* () {
            const records = yield this.read();
            return records.filter(record => record.chatId === chatId);
        }));
    }
    /**
     * Message bodies are clinical content. Left behind, they would outlive the
     * session and be readable by the next person to use the machine, so signing
     * out has to take them with it.
     *
     * Queued behind any in-flight write, otherwise a save already in progress
     * would put a record back moments after the clear.
     */
    clear() {
        if (!this.store) {
            return Promise.resolve();
        }
        return this.serialise(() => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                yield ((_a = this.store) === null || _a === void 0 ? void 0 : _a.removeItem(OUTBOX_KEY));
            }
            catch (_b) {
                // Nothing recoverable to do; the records remain until the TTL.
            }
        }));
    }
    hasUnresolved() {
        if (!this.store) {
            return Promise.resolve(false);
        }
        return this.serialise(() => __awaiter(this, void 0, void 0, function* () { return (yield this.read()).length > 0; }));
    }
    serialise(operation) {
        const result = this.mutations.then(operation, operation);
        this.mutations = result.catch(() => undefined);
        return result;
    }
    read() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const raw = yield ((_a = this.store) === null || _a === void 0 ? void 0 : _a.getItem(OUTBOX_KEY));
                if (!raw) {
                    return [];
                }
                const parsed = JSON.parse(raw);
                if (!Array.isArray(parsed)) {
                    return [];
                }
                return this.prune(parsed);
            }
            catch (_b) {
                // A corrupt blob must not wedge sending. Drop it and carry on.
                return [];
            }
        });
    }
    write(records) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                yield ((_a = this.store) === null || _a === void 0 ? void 0 : _a.setItem(OUTBOX_KEY, JSON.stringify(records)));
            }
            catch (_b) {
                // Storage full or unavailable: the send still proceeds in memory.
            }
        });
    }
    /**
     * Bounds the blob so a persistent failure cannot grow it without limit.
     * Anything older than the TTL is past any useful resend window. Order is
     * preserved: the newest records are kept by dropping from the front.
     */
    prune(records) {
        const cutoff = Date.now() - utils_1.HHNConstants.OUTBOX_TTL_MS;
        const live = records.filter(record => { var _a; return ((_a = record === null || record === void 0 ? void 0 : record.payload) === null || _a === void 0 ? void 0 : _a.messageId) && record.sentAt >= cutoff; });
        return live.length > utils_1.HHNConstants.MAX_OUTBOX_RECORDS
            ? live.slice(live.length - utils_1.HHNConstants.MAX_OUTBOX_RECORDS)
            : live;
    }
}
exports.MessageOutbox = MessageOutbox;
